import React from 'react';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useFeatureFlag } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import {
  DesktopNavigatorQuery,
  DesktopNavigatorQueryVariables,
  PageLayoutQuery,
  PageLayoutQueryVariables
} from '../../generated/types';
import { logger } from '../../lib/logger';
import { usePreserveUtmQueryParam } from '../../hooks/usePreserveUtmQueryParam';
import { useSessionQueryParams } from '../../hooks/useSessionQueryParams';
import { useIsAmexCustomer } from '../../hooks/useIsAmexCustomer';
import { usePageTracking } from '../../hooks/usePageTracking';
import { useSignifydSessionId } from '../../hooks/useSignifydSessionId';
import { usePreservePromoCodeInCookie } from '../../hooks/usePreservePromoCodeInCookie';
import { useSkippableQuery } from '../../hooks/useSkippableQuery';
import { EnableLazyLoad } from '../LazyLoad';
import { useCountry } from '../CountrySelector';
import { ErrorBoundary } from '../ErrorBoundary';
import { SitePromoBanner, SITE_PROMO_BANNER_FRAGMENT } from '../SitePromoBanner';
import { SiteNav, SITE_NAV_FRAGMENT } from '../SiteNav';
import { MinimalNav } from '../MinimalNav';
import { Notifications } from '../Notifications';
import { SiteFooter } from '../SiteFooter';
import { EmailSignup } from '../EmailSignup';
import { SignifydScript } from '../SignifydScript';

import './PageLayout.scss';

const DESKTOP_NAVIGATOR_TAB_EDITORIAL_UNIT_FRAGMENT = gql`
  fragment DesktopNavigatorTabEditorialUnitFragment on EditorialNavigatorUnit {
    anchorText
    id
    headline
    href
    imageSrc: image_source
    imageAlt: image_alt
  }
`;

const DESKTOP_NAVIGATOR_TAB_LIST_UNIT_FRAGMENT = gql`
  fragment DesktopNavigatorTabListUnitFragment on ListNavigatorUnit {
    id
    header
    subcategories: list {
      title
      href
    }
  }
`;

const DESKTOP_NAVIGATOR_TAB_FRAGMENT = gql`
  fragment DesktopNavigatorTabFragment on NavigatorItem {
    id
    name
    style {
      isBold: bold
      color
    }
    tabHeaderHref: href
    units {
      ...DesktopNavigatorTabListUnitFragment
      ...DesktopNavigatorTabEditorialUnitFragment
    }
  }
  ${DESKTOP_NAVIGATOR_TAB_LIST_UNIT_FRAGMENT}
  ${DESKTOP_NAVIGATOR_TAB_EDITORIAL_UNIT_FRAGMENT}
`;

const DESKTOP_NAVIGATOR_FRAGMENT = gql`
  fragment DesktopNavigatorFragment on Navigator {
    tabs: items {
      ...DesktopNavigatorTabFragment
    }
  }
  ${DESKTOP_NAVIGATOR_TAB_FRAGMENT}
`;

export const DESKTOP_NAVIGATOR_QUERY: TypedDocumentNode<
  DesktopNavigatorQuery,
  DesktopNavigatorQueryVariables
> = gql`
  query DesktopNavigatorQuery {
    desktopNavigator: navigator(platform: DESKTOP) {
      ...DesktopNavigatorFragment
    }
  }
  ${DESKTOP_NAVIGATOR_FRAGMENT}
`;

export const PAGE_LAYOUT_QUERY: TypedDocumentNode<PageLayoutQuery, PageLayoutQueryVariables> = gql`
  query PageLayoutQuery(
    $gender: String!
    $slug: String!
    $countryCode: String!
    $show_beauty: Boolean
  ) {
    displayPage(
      gender: $gender
      slug: $slug
      country_code: $countryCode
      show_beauty: $show_beauty
    ) {
      ...SitePromoBannerFragment
      ...SiteNavFragment
    }
  }
  ${SITE_NAV_FRAGMENT}
  ${SITE_PROMO_BANNER_FRAGMENT},
`;

export const PageLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();
  const { pathname } = useLocation();
  const includeHeader = pathname !== hrefFor.CheckoutPage();

  const isBeautyEnabled = useFeatureFlag('beauty', [{ default: false }, { if: true, then: true }]);

  const isDesktopSiteNavV2Enabled = useFeatureFlag('desktop-nav-v2', [
    { default: false },
    { if: true, then: true }
  ]);

  const { data: pageLayoutQueryData, error: pageLayoutQueryError } = useSkippableQuery<
    PageLayoutQuery,
    PageLayoutQueryVariables
  >(PAGE_LAYOUT_QUERY, {
    variables: {
      gender: 'women',
      slug: 'women',
      countryCode,
      show_beauty: isBeautyEnabled
    },
    skip: !includeHeader
  });

  const { data: desktopNavigatorQueryData, error: desktopNavigatorQueryError } = useSkippableQuery<
    DesktopNavigatorQuery,
    DesktopNavigatorQueryVariables
  >(DESKTOP_NAVIGATOR_QUERY, { skip: !isDesktopSiteNavV2Enabled });

  if (pageLayoutQueryError) {
    // Allow page to render as best it can and warn the error
    logger.warn('PAGE_LAYOUT', pageLayoutQueryError.message, pageLayoutQueryError);
  }

  if (desktopNavigatorQueryError) {
    // Allow page to render as best it can and warn the error
    logger.warn('PAGE_LAYOUT', desktopNavigatorQueryError.message, desktopNavigatorQueryError);
  }

  usePreserveUtmQueryParam();
  useSessionQueryParams();
  useIsAmexCustomer();
  usePageTracking();
  useSignifydSessionId({ createIfNotExists: true });
  usePreservePromoCodeInCookie();

  // This is here to trigger an 'Experiment Viewed' event for this feature flag
  useFeatureFlag('search-api-v2', [{ default: false }]);

  return (
    <div className="PageLayout">
      {includeHeader && (
        <ErrorBoundary FallbackComponent={MinimalNav}>
          <SitePromoBanner displayPage={pageLayoutQueryData?.displayPage} />

          <EnableLazyLoad>
            <SiteNav
              displayPage={pageLayoutQueryData?.displayPage}
              desktopNavigator={desktopNavigatorQueryData?.desktopNavigator}
            />
          </EnableLazyLoad>
        </ErrorBoundary>
      )}

      <EmailSignup />
      <Notifications />
      <SignifydScript />

      <main className="PageLayout__content">{children}</main>

      <SiteFooter />
    </div>
  );
};
